import { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import {
	ChevronLeft,
	ChevronRight,
	CalendarDays,
	Search,
	MapPin,
	Clock3,
	X,
	BadgeCheck,
	ShoppingBasket,
	Trash2,
	CheckCircle2,
	User,
	Mail,
	Phone,
	RotateCcw,
	ChevronDown,
} from 'lucide-react';
import './CourseCalendar.css';

const CALENDAR_EVENT_NAME = 'clevory:open-course-calendar';
const CALENDAR_STORAGE_KEY = 'clevory_course_calendar_payload';

const TRAINING_COLORS = ['#55afdc', '#717272'];

function hashString(value = '') {
	let hash = 0;
	const safeValue = String(value || 'formation');

	for (let i = 0; i < safeValue.length; i += 1) {
		hash = (hash * 31 + safeValue.charCodeAt(i)) >>> 0;
	}

	return hash;
}

function getTrainingColor(key = '') {
	return TRAINING_COLORS[hashString(key) % TRAINING_COLORS.length];
}

function getSessionColorKey(eventId, titleKey, start) {
	return [eventId || '', titleKey || '', start || ''].join('|');
}

function getSessionColor(eventId, titleKey, start) {
	return TRAINING_COLORS[
		hashString(getSessionColorKey(eventId, titleKey, start)) %
			TRAINING_COLORS.length
	];
}

const tunisianHolidays2026 = [
	{
		id: 'holiday-1',
		title: 'Nouvel An',
		start: '2026-01-01',
		allDay: true,
		extendedProps: {
			type: 'holiday',
			description: 'Jour férié en Tunisie',
			location: 'Tunisie',
		},
	},
	{
		id: 'holiday-2',
		title: "Fête de l'indépendance",
		start: '2026-03-20',
		allDay: true,
		extendedProps: {
			type: 'holiday',
			description: 'Jour férié national',
			location: 'Tunisie',
		},
	},
	{
		id: 'holiday-3',
		title: 'Aïd el-Fitr',
		start: '2026-03-20',
		end: '2026-03-23',
		allDay: true,
		extendedProps: {
			type: 'holiday',
			description: 'Fête religieuse (date approximative)',
			location: 'Tunisie',
		},
	},
	{
		id: 'holiday-4',
		title: 'Fête des Martyrs',
		start: '2026-04-09',
		allDay: true,
		extendedProps: {
			type: 'holiday',
			description: 'Jour férié national',
			location: 'Tunisie',
		},
	},
];

function getTitleKey(value) {
	return String(value || '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, ' ');
}

const EXCLUDED_TRAINING_TITLE_KEYS = new Set([
	'formation it sales',
	'it sales',
	'formation agentic ai',
	'agentic ai',
]);

function cleanTrainingTitle(value) {
	return String(value || '')
		.trim()
		.replace(/^formation\s*[-:–—]?\s*/i, '')
		.replace(/\s+/g, ' ');
}

function isExcludedTrainingTitle(value) {
	const rawKey = getTitleKey(value);
	const cleanedKey = getTitleKey(cleanTrainingTitle(value));

	return (
		EXCLUDED_TRAINING_TITLE_KEYS.has(rawKey) ||
		EXCLUDED_TRAINING_TITLE_KEYS.has(cleanedKey)
	);
}

function toDateOnly(value) {
	if (!value) return null;

	if (typeof value === 'string') {
		const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (match) return `${match[1]}-${match[2]}-${match[3]}`;
	}

	const date = toDate(value);
	return date ? toLocalDateKey(date) : null;
}

function toLocalDateKey(date) {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

function spansMultipleCalendarDays(start, end) {
	const startDay = toDateOnly(start);
	const endDay = toDateOnly(end);
	return Boolean(startDay && endDay && startDay !== endDay);
}

function isDateOnly(value) {
	return (
		Boolean(value) &&
		!String(value).includes('T') &&
		!String(value).includes(' ')
	);
}

function addOneDay(dateValue) {
	const date = toDate(dateValue);
	if (!date) return dateValue;

	date.setDate(date.getDate() + 1);
	return toLocalDateKey(date);
}

function normalizeEndDate(start, end, allDay) {
	if (!start) return end || null;

	if (!end) {
		return allDay && isDateOnly(start) ? addOneDay(start) : null;
	}

	if (allDay && isDateOnly(end)) {
		return addOneDay(end);
	}

	return end;
}

function toDate(value) {
	if (!value) return null;

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const text = String(value).trim();
	const dateOnlyMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (dateOnlyMatch) {
		const [, year, month, day] = dateOnlyMatch;
		const localDate = new Date(Number(year), Number(month) - 1, Number(day));
		return Number.isNaN(localDate.getTime()) ? null : localDate;
	}

	const date = new Date(text.replace(' ', 'T'));
	return Number.isNaN(date.getTime()) ? null : date;
}

function toIso(value) {
	const date = toDate(value);
	return date ? date.toISOString() : null;
}

function formatDateRangeEnd(endDate, shouldUseExclusiveEnd) {
	if (!shouldUseExclusiveEnd) return endDate;

	const adjustedEnd = new Date(endDate);
	adjustedEnd.setDate(adjustedEnd.getDate() - 1);
	return adjustedEnd;
}

function formatEventDate(start, end, allDay) {
	const startDate = toDate(start);
	const endDate = toDate(end);

	if (!startDate) return 'Date à confirmer';

	if (allDay) {
		if (!endDate) {
			return startDate.toLocaleDateString('fr-FR', {
				weekday: 'short',
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		}

		const startKey = toLocalDateKey(startDate);
		const endKey = toLocalDateKey(endDate);
		const shouldUseExclusiveEnd =
			Boolean(end) && String(end).includes('T00:00:00') && startKey !== endKey;
		const displayEnd = formatDateRangeEnd(endDate, shouldUseExclusiveEnd);

		if (toLocalDateKey(displayEnd) === startKey) {
			return startDate.toLocaleDateString('fr-FR', {
				weekday: 'short',
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		}

		return `${startDate.toLocaleDateString('fr-FR', {
			month: 'short',
			day: 'numeric',
		})} - ${displayEnd.toLocaleDateString('fr-FR', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})}`;
	}

	const startText = startDate.toLocaleString('fr-FR', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});

	if (!endDate) return startText;

	const endText = endDate.toLocaleString('fr-FR', {
		hour: '2-digit',
		minute: '2-digit',
	});

	return `${startText} - ${endText}`;
}

function formatSessionChipDate(start, end, allDay) {
	return formatEventDate(start, end, allDay);
}

function isEventPast(eventLike) {
	const now = new Date();
	const endDate = toDate(eventLike.end) || toDate(eventLike.start);

	if (!endDate) return false;

	if (eventLike.allDay) {
		endDate.setHours(23, 59, 59, 999);
	}

	return endDate.getTime() < now.getTime();
}

function normalizeSelectedEvent(event) {
	const extendedProps = event.extendedProps || {};
	const realStart = extendedProps.originalStart || event.start;
	const realEnd = extendedProps.originalEnd || event.end;

	return {
		id: extendedProps.originalEventId || event.id,
		title: event.title,
		start:
			realStart instanceof Date ? realStart.toISOString() : toIso(realStart),
		end: realEnd instanceof Date ? realEnd.toISOString() : toIso(realEnd),
		allDay: event.allDay,
		location: extendedProps.location || '',
		description: extendedProps.description || '',
		image: extendedProps.image || '',
		type: extendedProps.type || 'course',
		trainingColor:
			extendedProps.trainingColor ||
			getSessionColor(
				extendedProps.originalEventId || event.id,
				getTitleKey(event.title),
				extendedProps.originalStart || event.start,
			),
	};
}

function normalizeCalendarEvent(event) {
	const extendedProps = event.extendedProps || {};
	const realStart = extendedProps.originalStart || event.start;
	const realEnd = extendedProps.originalEnd || event.end;

	return {
		id: extendedProps.originalEventId || event.id,
		title: event.title,
		start:
			realStart instanceof Date ? realStart.toISOString() : toIso(realStart),
		end: realEnd instanceof Date ? realEnd.toISOString() : toIso(realEnd),
		allDay: Boolean(event.allDay),
		location: extendedProps.location || '',
		description: extendedProps.description || '',
		image: extendedProps.image || '',
		type: extendedProps.type || 'course',
		trainingColor:
			extendedProps.trainingColor ||
			getSessionColor(
				extendedProps.originalEventId || event.id,
				getTitleKey(event.title),
				extendedProps.originalStart || event.start,
			),
	};
}

function mapIncomingEventToCalendarEvent(item, index = 0) {
	const rawStart = item?.start || item?.startdate || item?.date_begin || null;
	const rawEnd = item?.end || item?.enddate || item?.date_end || null;
	const title = cleanTrainingTitle(
		item?.title || item?.name || item?.display_name || `Événement ${index + 1}`,
	);

	if (isExcludedTrainingTitle(title)) return null;

	const isDateOnlyRange =
		!String(rawStart || '').includes('T') &&
		!String(rawStart || '').includes(' ') &&
		!String(rawEnd || '').includes('T') &&
		!String(rawEnd || '').includes(' ');
	const isMultiDayTraining = spansMultipleCalendarDays(rawStart, rawEnd);
	const allDay = Boolean(item?.allDay || isDateOnlyRange || isMultiDayTraining);

	const start = allDay ? toDateOnly(rawStart) || rawStart : rawStart;
	const end = allDay
		? normalizeEndDate(start, toDateOnly(rawEnd) || rawEnd, true)
		: normalizeEndDate(rawStart, rawEnd, false);
	const titleKey = getTitleKey(title);
	const eventId = item?.id || `dynamic-${index + 1}`;
	const sessionColor = getSessionColor(eventId, titleKey, start);

	return {
		id: eventId,
		title,
		start,
		end,
		allDay,
		display: 'block',
		extendedProps: {
			type: item?.type || 'course',
			location: item?.location || item?.address || '',
			image: item?.image || '',
			description: item?.description || '',
			courseid: item?.courseid || null,
			raw: item,
			originalStart: rawStart,
			originalEnd: rawEnd,
			titleKey,
			trainingColor: sessionColor,
		},
	};
}

function addDaysToDateOnly(dateOnly, days) {
	const date = toDate(dateOnly);
	if (!date) return null;
	date.setDate(date.getDate() + days);
	return toLocalDateKey(date);
}

function getCalendarEventRealStart(event) {
	return event?.extendedProps?.originalStart || event?.start || null;
}

function getCalendarEventRealEnd(event) {
	return (
		event?.extendedProps?.originalEnd || event?.end || event?.start || null
	);
}

function expandCourseEventIntoDailyBars(event) {
	if (!event || event.extendedProps?.type === 'holiday') return [event];

	const firstDay = toDateOnly(getCalendarEventRealStart(event));
	const rawEndDay = toDateOnly(getCalendarEventRealEnd(event)) || firstDay;

	if (!firstDay || !rawEndDay) return [event];

	const firstDate = toDate(firstDay);
	const lastDate = toDate(rawEndDay);
	if (!firstDate || !lastDate || lastDate.getTime() < firstDate.getTime())
		return [event];

	const totalDays =
		Math.round((lastDate.getTime() - firstDate.getTime()) / 86400000) + 1;
	const titleKey = getTrainingKey(event);
	const color =
		event.extendedProps?.trainingColor ||
		getSessionColor(event.id, titleKey, getCalendarEventRealStart(event));

	return Array.from({ length: totalDays }, (_, dayIndex) => {
		const dayStart = addDaysToDateOnly(firstDay, dayIndex);
		const dayEnd = addDaysToDateOnly(firstDay, dayIndex + 1);
		const isFirst = dayIndex === 0;
		const isLast = dayIndex === totalDays - 1;

		return {
			...event,
			id: `${event.id}__bar__${dayStart}`,
			groupId: String(event.id),
			start: dayStart,
			end: dayEnd,
			allDay: true,
			display: 'block',
			extendedProps: {
				...(event.extendedProps || {}),
				originalEventId: event.id,
				originalStart: getCalendarEventRealStart(event),
				originalEnd: getCalendarEventRealEnd(event),
				barSegment:
					totalDays === 1
						? 'single'
						: isFirst
							? 'first'
							: isLast
								? 'last'
								: 'middle',
				barDayIndex: dayIndex,
				barTotalDays: totalDays,
				showBarTitle: isFirst,
				showEndLabel: totalDays > 1 && isLast,
				trainingColor: color,
			},
		};
	});
}

function resolveIncomingEvents(calendarData) {
	if (!calendarData) return [];

	if (Array.isArray(calendarData)) {
		return calendarData.map(mapIncomingEventToCalendarEvent).filter(Boolean);
	}

	if (Array.isArray(calendarData?.events)) {
		return calendarData.events
			.map(mapIncomingEventToCalendarEvent)
			.filter(Boolean);
	}

	return [];
}

function EventCard({ info, onHoverStart, onHoverEnd, isMobile }) {
	const event = info.event;
	const isHoliday = event.extendedProps.type === 'holiday';
	const isSyntheticTrainingBar =
		!isHoliday && Boolean(event.extendedProps?.barSegment);
	const showTitle =
		isHoliday ||
		Boolean(event.extendedProps?.showBarTitle) ||
		(!isSyntheticTrainingBar && info.isStart);
	const showEndLabel = !isHoliday && Boolean(event.extendedProps?.showEndLabel);
	const segment = event.extendedProps?.barSegment || 'single';

	const accentColor =
		event.extendedProps?.trainingColor ||
		getSessionColor(event.id, getTitleKey(event.title), event.start);

	return (
		<div
			className={`fc-premium-event ${
				isHoliday ? 'fc-premium-event--holiday' : 'fc-premium-event--course'
			} ${showTitle ? 'fc-premium-event--start' : 'fc-premium-event--continuation'} fc-premium-event--segment-${segment}`}
			style={{ '--event-accent': isHoliday ? '#717272' : accentColor }}
			onMouseEnter={(e) => {
				if (!isMobile) onHoverStart(event, e.currentTarget);
			}}
			onMouseLeave={() => {
				if (!isMobile) onHoverEnd();
			}}>
			<span className='fc-premium-event__bar' />
			<div className='fc-premium-event__dot' />

			<div className='fc-premium-event__content'>
				{showTitle && !showEndLabel ? (
					<div className='fc-premium-event__title'>{event.title}</div>
				) : null}

				{showEndLabel ? (
					<div className='fc-premium-event__end-label'>Fin de formation</div>
				) : null}

				{!isHoliday && info.timeText && showTitle && !showEndLabel ? (
					<div className='fc-premium-event__time'>{info.timeText}</div>
				) : null}
			</div>
		</div>
	);
}

function EventHoverTooltip({
	data,
	relatedSessions = [],
	onCloseDelay,
	onKeepOpen,
	onSubscribe,
	isAlreadySubscribed,
	onSelectSession,
}) {
	if (!data) return null;

	const isHoliday = data.type === 'holiday';
	const hasPassed = isEventPast(data);
	const canSubscribe = !isHoliday && !hasPassed;
	const alreadySubscribed = isAlreadySubscribed(data.id);

	return (
		<div
			className={`event-hover-tooltip event-hover-tooltip--${data.placement}`}
			style={{ top: data.top, left: data.left }}
			onMouseEnter={onKeepOpen}
			onMouseLeave={onCloseDelay}>
			<div className='event-hover-tooltip__media'>
				{data.image ? (
					<img src={data.image} alt={data.title} />
				) : (
					<div className='event-hover-tooltip__media-placeholder'>
						<CalendarDays size={24} />
					</div>
				)}
			</div>

			<div className='event-hover-tooltip__body'>
				<div className='event-hover-tooltip__badges'>
					<span
						className={`event-hover-tooltip__badge ${
							isHoliday
								? 'event-hover-tooltip__badge--holiday'
								: 'event-hover-tooltip__badge--course'
						}`}>
						{isHoliday ? 'Jour férié' : 'Formation'}
					</span>

					{hasPassed && !isHoliday ? (
						<span className='event-hover-tooltip__status event-hover-tooltip__status--past'>
							Passé
						</span>
					) : null}

					{!hasPassed && !isHoliday ? (
						<span className='event-hover-tooltip__status event-hover-tooltip__status--open'>
							Ouvert
						</span>
					) : null}
				</div>

				<h4 className='event-hover-tooltip__title'>{data.title}</h4>

				<div className='event-hover-tooltip__meta'>
					<div className='event-hover-tooltip__meta-item'>
						<Clock3 size={14} />
						<span>{formatEventDate(data.start, data.end, data.allDay)}</span>
					</div>

					{data.location ? (
						<div className='event-hover-tooltip__meta-item'>
							<MapPin size={14} />
							<span>{data.location}</span>
						</div>
					) : null}
				</div>

				{relatedSessions.length > 1 && !isHoliday ? (
					<div className='event-hover-tooltip__sessions'>
						<div className='event-hover-tooltip__sessions-list'>
							{relatedSessions.map((session) => (
								<button
									key={session.id}
									type='button'
									className={`event-hover-tooltip__session-chip ${
										String(session.id) === String(data.id)
											? 'event-hover-tooltip__session-chip--active'
											: ''
									}`}
									onClick={() => onSelectSession(session)}>
									{formatSessionChipDate(
										session.start,
										session.end,
										session.allDay,
									)}
								</button>
							))}
						</div>
					</div>
				) : null}

				<div className='event-hover-tooltip__actions'>
					{canSubscribe && !alreadySubscribed ? (
						<button
							type='button'
							className='event-hover-tooltip__btn'
							onClick={() => onSubscribe(data)}>
							<BadgeCheck size={16} />
							<span>S’inscrire</span>
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
}

function EventMobileModal({
	eventData,
	relatedSessions = [],
	onClose,
	onSubscribe,
	isAlreadySubscribed,
	onSelectSession,
}) {
	if (!eventData) return null;

	const isHoliday = eventData.type === 'holiday';
	const hasPassed = isEventPast(eventData);
	const canSubscribe = !isHoliday && !hasPassed;
	const alreadySubscribed = isAlreadySubscribed(eventData.id);

	return (
		<div className='course-calendar-modal__backdrop' onClick={onClose}>
			<div
				className='course-calendar-modal'
				onClick={(e) => e.stopPropagation()}>
				<button
					type='button'
					className='course-calendar-modal__close'
					onClick={onClose}
					aria-label='Fermer les détails'>
					<X size={18} />
				</button>

				<div className='course-calendar-modal__body'>
					<h3 className='course-calendar-modal__title'>{eventData.title}</h3>

					<div className='course-calendar-modal__meta'>
						<div className='course-calendar-modal__meta-item'>
							<Clock3 size={16} />
							<span>
								{formatEventDate(
									eventData.start,
									eventData.end,
									eventData.allDay,
								)}
							</span>
						</div>

						{eventData.location ? (
							<div className='course-calendar-modal__meta-item'>
								<MapPin size={16} />
								<span>{eventData.location}</span>
							</div>
						) : null}
					</div>

					{relatedSessions.length > 1 && !isHoliday ? (
						<div className='course-calendar-modal__sessions'>
							<div className='course-calendar-modal__sessions-list'>
								{relatedSessions.map((session) => (
									<button
										key={session.id}
										type='button'
										className={`course-calendar-modal__session-chip ${
											String(session.id) === String(eventData.id)
												? 'course-calendar-modal__session-chip--active'
												: ''
										}`}
										onClick={() => onSelectSession(session)}>
										{formatSessionChipDate(
											session.start,
											session.end,
											session.allDay,
										)}
									</button>
								))}
							</div>
						</div>
					) : null}

					<p className='course-calendar-modal__description'>
						{eventData.description || 'Aucune description disponible.'}
					</p>

					<div className='course-calendar-modal__actions'>
						{canSubscribe && !alreadySubscribed ? (
							<button
								type='button'
								className='course-calendar-modal__subscribe-btn'
								onClick={() => onSubscribe(eventData)}>
								<BadgeCheck size={17} />
								<span>S’inscrire à cet événement</span>
							</button>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
}

function TrainingSidebarItem({
	group,
	isActive,
	isExpanded,
	isMobile,
	onToggleFilter,
	onToggleExpand,
	onSelectSession,
}) {
	const sessionLabel =
		group.sessions.length > 1
			? `${group.sessions.length} sessions disponibles`
			: '1 session disponible';

	const firstSession = group.sessions[0];
	const accentColor = group.color || getTrainingColor(group.id);

	return (
		<div
			className={`course-calendar__training-filter ${
				isActive ? 'course-calendar__training-filter--active' : ''
			} ${isExpanded ? 'course-calendar__training-filter--expanded' : ''}`}
			style={{ '--training-accent': accentColor }}
			onMouseEnter={() => {
				if (!isMobile && group.sessions.length > 1)
					onToggleExpand(group.id, true);
			}}
			onMouseLeave={() => {
				if (!isMobile) onToggleExpand(group.id, false);
			}}>
			<div
				className='course-calendar__training-main'
				role='button'
				tabIndex={0}
				onClick={() => {
					onToggleFilter(group.id);
					onSelectSession(firstSession);
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onToggleFilter(group.id);
						onSelectSession(firstSession);
					}
				}}>
				<span className='course-calendar__training-dot' />

				<span className='course-calendar__training-info'>
					<strong>{group.title}</strong>
					<small>{sessionLabel}</small>
					<em>
						{formatEventDate(
							firstSession.start,
							firstSession.end,
							firstSession.allDay,
						)}
					</em>
				</span>

				{group.sessions.length > 1 ? (
					<button
						type='button'
						className='course-calendar__training-expand'
						onClick={(e) => {
							e.stopPropagation();
							onToggleExpand(group.id);
						}}
						aria-label='Afficher les sessions'>
						<ChevronDown size={16} />
					</button>
				) : null}
			</div>

			{group.sessions.length > 1 ? (
				<div className='course-calendar__training-sessions'>
					<div className='course-calendar__training-sessions-title'>
						Autres dates disponibles
					</div>
					{group.sessions.map((session) => (
						<button
							key={session.id}
							type='button'
							className='course-calendar__training-session-chip'
							onClick={() => onSelectSession(session)}>
							<span>
								{formatEventDate(session.start, session.end, session.allDay)}
							</span>
						</button>
					))}
				</div>
			) : null}
		</div>
	);
}

function SubscriptionCart({
	items,
	onRemove,
	onOpenForm,
	onCloseMobile,
	isMobileOpen,
	onMouseEnter,
	onMouseLeave,
}) {
	return (
		<div
			className={`subscription-cart-panel ${
				isMobileOpen ? 'subscription-cart-panel--mobile-open' : ''
			}`}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}>
			<div className='subscription-cart-panel__header'>
				<h4>Événements sélectionnés</h4>

				<button
					type='button'
					className='subscription-cart-panel__close'
					onClick={onCloseMobile}
					aria-label='Fermer le panier'>
					<X size={16} />
				</button>
			</div>

			{items.length ? (
				<>
					<div className='subscription-cart-panel__list'>
						{items.map((item) => (
							<div key={item.id} className='subscription-cart-panel__item'>
								<div className='subscription-cart-panel__item-content'>
									<strong>{item.title}</strong>
									<span>
										{formatEventDate(item.start, item.end, item.allDay)}
									</span>
								</div>

								<button
									type='button'
									className='subscription-cart-panel__delete'
									onClick={() => onRemove(item.id)}
									aria-label={`Supprimer ${item.title}`}>
									<Trash2 size={16} />
								</button>
							</div>
						))}
					</div>

					<button
						type='button'
						className='subscription-cart-panel__validate'
						onClick={onOpenForm}>
						Valider mes inscriptions
					</button>
				</>
			) : (
				<p className='subscription-cart-panel__empty'>
					Aucun événement sélectionné pour le moment.
				</p>
			)}
		</div>
	);
}

function SubscriptionFormModal({
	items,
	formData,
	onChange,
	onSubmit,
	onClose,
}) {
	return (
		<div className='course-calendar-modal__backdrop' onClick={onClose}>
			<div
				className='subscription-form-modal'
				onClick={(e) => e.stopPropagation()}>
				<button
					type='button'
					className='course-calendar-modal__close'
					onClick={onClose}
					aria-label='Fermer le formulaire'>
					<X size={18} />
				</button>

				<div className='subscription-form-modal__body'>
					<h3 className='subscription-form-modal__title'>
						Valider vos inscriptions
					</h3>

					<div className='subscription-form-modal__tags'>
						{items.map((item) => (
							<span key={item.id} className='subscription-form-modal__tag'>
								{item.title}
							</span>
						))}
					</div>

					<form className='subscription-form' onSubmit={onSubmit}>
						<div className='subscription-form__grid'>
							<label className='subscription-form__field'>
								<span>
									<User size={16} />
									Nom complet
								</span>
								<input
									type='text'
									name='fullName'
									value={formData.fullName}
									onChange={onChange}
									required
								/>
							</label>

							<label className='subscription-form__field'>
								<span>
									<Mail size={16} />
									Adresse e-mail
								</span>
								<input
									type='email'
									name='email'
									value={formData.email}
									onChange={onChange}
									required
								/>
							</label>

							<label className='subscription-form__field'>
								<span>
									<Phone size={16} />
									Téléphone
								</span>
								<input
									type='tel'
									name='phone'
									value={formData.phone}
									onChange={onChange}
									required
								/>
							</label>

							<label className='subscription-form__field subscription-form__field--full'>
								<span>Message complémentaire</span>
								<textarea
									name='message'
									value={formData.message}
									onChange={onChange}
									rows={4}
								/>
							</label>
						</div>

						<button type='submit' className='subscription-form__submit'>
							Souscrire
						</button>
					</form>
				</div>
			</div>
		</div>
	);
}

function getTrainingKey(event) {
	return getTitleKey(event?.title || '');
}

export default function CourseCalendar({ calendarData = null }) {
	const calendarRef = useRef(null);
	const calendarWrapRef = useRef(null);
	const transitionTimeoutRef = useRef(null);
	const toastTimeoutRef = useRef(null);
	const cartHoverTimeoutRef = useRef(null);
	const tooltipCloseTimeoutRef = useRef(null);

	const [externalCalendarData, setExternalCalendarData] = useState(() => {
		if (calendarData) return calendarData;

		try {
			const stored = sessionStorage.getItem(CALENDAR_STORAGE_KEY);
			return stored ? JSON.parse(stored) : null;
		} catch (error) {
			console.error('Erreur lecture sessionStorage calendrier:', error);
			return null;
		}
	});

	const [search, setSearch] = useState('');
	const [currentTitle, setCurrentTitle] = useState('');
	const [activeView, setActiveView] = useState('dayGridMonth');
	const [calendarTransition, setCalendarTransition] = useState('');
	const [subscriptionItems, setSubscriptionItems] = useState([]);
	const [toastOpen, setToastOpen] = useState(false);
	const [toastText, setToastText] = useState('');
	const [isCartHoverOpen, setIsCartHoverOpen] = useState(false);
	const [isCartMobileOpen, setIsCartMobileOpen] = useState(false);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [hoveredEventData, setHoveredEventData] = useState(null);
	const [mobileEventData, setMobileEventData] = useState(null);
	const [activeTrainingIds, setActiveTrainingIds] = useState([]);
	const [expandedTrainingKey, setExpandedTrainingKey] = useState(null);
	const [focusedTrainingDay, setFocusedTrainingDay] = useState(null);
	const [isMobile, setIsMobile] = useState(
		typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
	);

	const [formData, setFormData] = useState({
		fullName: '',
		email: '',
		phone: '',
		message: '',
	});

	useEffect(() => {
		if (calendarData) {
			setExternalCalendarData(calendarData);

			try {
				sessionStorage.setItem(
					CALENDAR_STORAGE_KEY,
					JSON.stringify(calendarData),
				);
			} catch (error) {
				console.error('Erreur sauvegarde sessionStorage calendrier:', error);
			}
		}
	}, [calendarData]);

	useEffect(() => {
		const handleExternalCalendarOpen = (event) => {
			const payload = event?.detail || null;
			if (!payload) return;

			setExternalCalendarData(payload);

			try {
				sessionStorage.setItem(CALENDAR_STORAGE_KEY, JSON.stringify(payload));
			} catch (error) {
				console.error('Erreur sauvegarde sessionStorage calendrier:', error);
			}
		};

		window.addEventListener(CALENDAR_EVENT_NAME, handleExternalCalendarOpen);

		return () => {
			window.removeEventListener(
				CALENDAR_EVENT_NAME,
				handleExternalCalendarOpen,
			);
		};
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
			if (window.innerWidth <= 768) setHoveredEventData(null);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			if (transitionTimeoutRef.current)
				clearTimeout(transitionTimeoutRef.current);
			if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
			if (cartHoverTimeoutRef.current)
				clearTimeout(cartHoverTimeoutRef.current);
			if (tooltipCloseTimeoutRef.current)
				clearTimeout(tooltipCloseTimeoutRef.current);
		};
	}, []);

	const dynamicEvents = useMemo(
		() => resolveIncomingEvents(externalCalendarData),
		[externalCalendarData],
	);

	const effectiveCourseEvents = useMemo(() => {
		return dynamicEvents && dynamicEvents.length
			? dynamicEvents.filter((event) => !isExcludedTrainingTitle(event.title))
			: [];
	}, [dynamicEvents]);

	const trainingGroups = useMemo(() => {
		const map = new Map();

		effectiveCourseEvents.forEach((event) => {
			if (isExcludedTrainingTitle(event.title)) return;

			const key = getTrainingKey(event);

			if (!map.has(key)) {
				map.set(key, {
					id: key,
					title: event.title,
					color:
						event.extendedProps?.trainingColor ||
						getSessionColor(event.id, key, event.start),
					sessions: [],
				});
			}

			map.get(key).sessions.push({
				id: event.id,
				title: event.title,
				start: event.start,
				end: event.end,
				allDay: event.allDay,
				location: event.extendedProps?.location || '',
				description: event.extendedProps?.description || '',
				image: event.extendedProps?.image || '',
				type: event.extendedProps?.type || 'course',
				trainingColor:
					event.extendedProps?.trainingColor ||
					getSessionColor(event.id, key, event.start),
			});
		});

		return Array.from(map.values())
			.map((group) => ({
				...group,
				sessions: group.sessions.sort((a, b) => {
					const da = toDate(a.start)?.getTime() || 0;
					const db = toDate(b.start)?.getTime() || 0;
					return da - db;
				}),
			}))
			.sort((a, b) => {
				const da = toDate(a.sessions[0]?.start)?.getTime() || 0;
				const db = toDate(b.sessions[0]?.start)?.getTime() || 0;
				return da - db;
			});
	}, [effectiveCourseEvents]);

	const activeTrainingSet = useMemo(
		() => new Set(activeTrainingIds),
		[activeTrainingIds],
	);

	const calendarDisplayCourseEvents = useMemo(() => {
		return effectiveCourseEvents.flatMap(expandCourseEventIntoDailyBars);
	}, [effectiveCourseEvents]);

	const allEvents = useMemo(() => {
		return [...calendarDisplayCourseEvents, ...tunisianHolidays2026];
	}, [calendarDisplayCourseEvents]);

	const filteredEvents = useMemo(() => {
		const query = search.trim().toLowerCase();

		return allEvents.filter((item) => {
			const isHoliday = item.extendedProps?.type === 'holiday';
			const titleKey = getTrainingKey(item);

			if (activeTrainingSet.size > 0 && !isHoliday) {
				if (!activeTrainingSet.has(titleKey)) return false;
			}

			if (!query) return true;

			const location = item.extendedProps?.location || '';
			const description = item.extendedProps?.description || '';

			return (
				item.title.toLowerCase().includes(query) ||
				location.toLowerCase().includes(query) ||
				description.toLowerCase().includes(query)
			);
		});
	}, [allEvents, search, activeTrainingSet]);

	const courseEventsByTitle = useMemo(() => {
		const map = new Map();

		effectiveCourseEvents.forEach((event) => {
			if (isExcludedTrainingTitle(event.title)) return;

			const key = getTrainingKey(event);

			if (!map.has(key)) {
				map.set(key, []);
			}

			map.get(key).push(
				normalizeCalendarEvent({
					...event,
					start: event.start ? toDate(event.start) : null,
					end: event.end ? toDate(event.end) : null,
					allDay: event.allDay,
					extendedProps: event.extendedProps,
				}),
			);
		});

		map.forEach((sessions) => {
			sessions.sort((a, b) => {
				const da = toDate(a.start)?.getTime() || 0;
				const db = toDate(b.start)?.getTime() || 0;
				return da - db;
			});
		});

		return map;
	}, [effectiveCourseEvents]);

	const getRelatedSessions = (eventData) => {
		if (!eventData?.title) return [];
		return courseEventsByTitle.get(getTitleKey(eventData.title)) || [];
	};

	const toggleTrainingFilter = (trainingId) => {
		setActiveTrainingIds((prev) =>
			prev.includes(trainingId)
				? prev.filter((id) => id !== trainingId)
				: [...prev, trainingId],
		);
	};

	const resetTrainingFilters = () => {
		setActiveTrainingIds([]);
		setExpandedTrainingKey(null);
		setFocusedTrainingDay(null);
	};

	const toggleTrainingSessions = (trainingId, forceValue = null) => {
		setExpandedTrainingKey((prev) => {
			if (forceValue === true) return trainingId;
			if (forceValue === false) return prev === trainingId ? null : prev;
			return prev === trainingId ? null : trainingId;
		});
	};

	const focusSessionOnCalendar = (session) => {
		const api = calendarRef.current?.getApi();
		const focusedDay = toDateOnly(session?.start);
		const date = toDate(session?.start);

		if (focusedDay) {
			setFocusedTrainingDay(focusedDay);
		}

		if (api && date) {
			api.gotoDate(date);
			setTimeout(updateCurrentTitle, 20);
		}

		window.setTimeout(() => {
			const cell = document.querySelector(
				'.fc-daygrid-day.course-calendar__focused-start-day',
			);
			if (cell) {
				cell.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center',
				});
			}
		}, 80);

		if (isMobile) {
			setMobileEventData(session);
		}
	};

	const isAlreadySubscribed = (eventId) =>
		subscriptionItems.some((item) => item.id === eventId);

	const showToast = (message) => {
		if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);

		setToastText(message);
		setToastOpen(true);

		toastTimeoutRef.current = setTimeout(() => {
			setToastOpen(false);
		}, 3200);
	};

	const openCartHover = () => {
		if (cartHoverTimeoutRef.current) clearTimeout(cartHoverTimeoutRef.current);
		setIsCartHoverOpen(true);
	};

	const closeCartHoverWithDelay = () => {
		if (cartHoverTimeoutRef.current) clearTimeout(cartHoverTimeoutRef.current);

		cartHoverTimeoutRef.current = setTimeout(() => {
			setIsCartHoverOpen(false);
		}, 650);
	};

	const hideTooltipWithDelay = () => {
		if (tooltipCloseTimeoutRef.current)
			clearTimeout(tooltipCloseTimeoutRef.current);

		tooltipCloseTimeoutRef.current = setTimeout(() => {
			setHoveredEventData(null);
		}, 180);
	};

	const keepTooltipOpen = () => {
		if (tooltipCloseTimeoutRef.current)
			clearTimeout(tooltipCloseTimeoutRef.current);
	};

	const openHoverTooltip = (event, targetElement) => {
		if (isMobile || !calendarWrapRef.current || !targetElement) return;

		keepTooltipOpen();

		const eventData = normalizeCalendarEvent(event);
		const targetRect = targetElement.getBoundingClientRect();
		const wrapRect = calendarWrapRef.current.getBoundingClientRect();

		const tooltipWidth = 360;
		const gap = 14;
		const topBase = targetRect.top - wrapRect.top;
		const centerY = topBase + targetRect.height / 2;
		const preferredTop = centerY - 180;

		let placement = 'right';
		let left = targetRect.right - wrapRect.left + gap;

		if (left + tooltipWidth > wrapRect.width - 8) {
			placement = 'left';
			left = targetRect.left - wrapRect.left - tooltipWidth - gap;
		}

		if (left < 8) {
			left = 8;
			placement = 'right';
		}

		const estimatedHeight = 380;
		let top = preferredTop;
		const maxTop = wrapRect.height - estimatedHeight - 8;
		if (top < 8) top = 8;
		if (top > maxTop) top = Math.max(8, maxTop);

		setHoveredEventData({
			...eventData,
			top,
			left,
			placement,
		});
	};

	const handleSelectSessionFromTooltip = (session) => {
		setHoveredEventData((prev) =>
			prev
				? {
						...prev,
						...session,
					}
				: null,
		);
		focusSessionOnCalendar(session);
	};

	const handleSelectSessionFromMobile = (session) => {
		setMobileEventData(session);
		focusSessionOnCalendar(session);
	};

	const handleSubscribeEvent = (eventLike) => {
		const normalized =
			eventLike.extendedProps !== undefined
				? normalizeSelectedEvent(eventLike)
				: eventLike;

		if (normalized.type === 'holiday' || isEventPast(normalized)) return;

		if (isAlreadySubscribed(normalized.id)) {
			showToast('Cet événement est déjà dans votre panier.');
			openCartHover();
			setHoveredEventData(null);
			setMobileEventData(null);
			return;
		}

		setSubscriptionItems((prev) => [...prev, normalized]);
		setHoveredEventData(null);
		setMobileEventData(null);
		showToast(
			`Événement ajouté. Vous avez ${subscriptionItems.length + 1} inscription(s).`,
		);
		openCartHover();
	};

	const handleRemoveSubscription = (eventId) => {
		setSubscriptionItems((prev) => prev.filter((item) => item.id !== eventId));
	};

	const applyMonthTransition = (direction) => {
		if (transitionTimeoutRef.current)
			clearTimeout(transitionTimeoutRef.current);

		setCalendarTransition(
			direction === 'next'
				? 'course-calendar__calendar-wrap--slide-next'
				: 'course-calendar__calendar-wrap--slide-prev',
		);

		transitionTimeoutRef.current = setTimeout(() => {
			setCalendarTransition('');
		}, 460);
	};

	const updateCurrentTitle = () => {
		const api = calendarRef.current?.getApi();
		if (!api) return;
		setCurrentTitle(api.view.title);
	};

	const handlePrev = () => {
		applyMonthTransition('prev');
		calendarRef.current?.getApi()?.prev();
		setTimeout(updateCurrentTitle, 20);
	};

	const handleNext = () => {
		applyMonthTransition('next');
		calendarRef.current?.getApi()?.next();
		setTimeout(updateCurrentTitle, 20);
	};

	const handleToday = () => {
		applyMonthTransition('next');
		calendarRef.current?.getApi()?.today();
		setTimeout(updateCurrentTitle, 20);
	};

	const changeCalendarView = (viewName) => {
		calendarRef.current?.getApi()?.changeView(viewName);
		setActiveView(viewName);
		setTimeout(updateCurrentTitle, 20);
	};

	const handleMonthView = () => {
		changeCalendarView('dayGridMonth');
	};

	const handleWeekView = () => {
		changeCalendarView('timeGridWeek');
	};

	const handleDayView = () => {
		changeCalendarView('timeGridDay');
	};

	const handleListView = () => {
		changeCalendarView('listMonth');
	};

	const handleFormChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleFormSubmit = (e) => {
		e.preventDefault();

		setSubmitSuccess(true);
		setSubscriptionItems([]);
		setIsFormOpen(false);
		setIsCartMobileOpen(false);
		setIsCartHoverOpen(false);
		setFormData({
			fullName: '',
			email: '',
			phone: '',
			message: '',
		});

		showToast('Vos inscriptions ont bien été enregistrées.');
		setTimeout(() => setSubmitSuccess(false), 2600);
	};

	return (
		<section
			id='course-calendar-section'
			className='course-calendar course-calendar--premium'>
			<div className='course-calendar__shell'>
				<div className='course-calendar__toolbar'>
					<div className='course-calendar__toolbar-left'>
						<div className='course-calendar__nav'>
							<button
								type='button'
								className='course-calendar__nav-btn'
								onClick={handlePrev}
								aria-label='Précédent'>
								<ChevronLeft size={20} />
							</button>

							<button
								type='button'
								className='course-calendar__nav-btn'
								onClick={handleNext}
								aria-label='Suivant'>
								<ChevronRight size={20} />
							</button>

							<button
								type='button'
								className='course-calendar__today-btn'
								onClick={handleToday}>
								Aujourd’hui
							</button>
						</div>

						<h2 className='course-calendar__month-title'>
							{currentTitle || 'Calendrier'}
						</h2>

						<div className='course-calendar__view-switcher'>
							<button
								type='button'
								onClick={handleMonthView}
								aria-pressed={activeView === 'dayGridMonth'}>
								Mois
							</button>
							<button
								type='button'
								onClick={handleWeekView}
								aria-pressed={activeView === 'timeGridWeek'}>
								Semaine
							</button>
							<button
								type='button'
								onClick={handleDayView}
								aria-pressed={activeView === 'timeGridDay'}>
								Jour
							</button>
							<button
								type='button'
								onClick={handleListView}
								aria-pressed={activeView === 'listMonth'}>
								Agenda
							</button>
						</div>
					</div>

					<div className='course-calendar__toolbar-right'>
						<div className='course-calendar__search'>
							<Search size={18} />
							<input
								type='text'
								placeholder='Rechercher événements ou jours fériés'
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</div>
					</div>
				</div>

				<div className='course-calendar__workspace'>
					<aside className='course-calendar__sidebar'>
						<div className='course-calendar__sidebar-header'>
							<div>
								<span>Filtres</span>
								<h3>Formations</h3>
							</div>

							<button
								type='button'
								className='course-calendar__reset-btn'
								onClick={resetTrainingFilters}
								disabled={!activeTrainingIds.length}>
								<RotateCcw size={15} />
							</button>
						</div>

						<div className='course-calendar__training-list'>
							{trainingGroups.length ? (
								trainingGroups.map((group) => (
									<TrainingSidebarItem
										key={group.id}
										group={group}
										isMobile={isMobile}
										isActive={activeTrainingSet.has(group.id)}
										isExpanded={expandedTrainingKey === group.id}
										onToggleFilter={toggleTrainingFilter}
										onToggleExpand={toggleTrainingSessions}
										onSelectSession={focusSessionOnCalendar}
									/>
								))
							) : (
								<p className='course-calendar__sidebar-empty'>
									Aucune formation disponible.
								</p>
							)}
						</div>
					</aside>

					<div
						ref={calendarWrapRef}
						className={`course-calendar__calendar-wrap ${calendarTransition}`}>
						<FullCalendar
							ref={calendarRef}
							plugins={[
								dayGridPlugin,
								timeGridPlugin,
								listPlugin,
								interactionPlugin,
							]}
							locale={frLocale}
							initialView='dayGridMonth'
							headerToolbar={false}
							height='auto'
							selectable
							editable={false}
							dayMaxEvents={false}
							weekends
							nowIndicator
							displayEventEnd
							eventDisplay='block'
							allDayMaintainDuration
							forceEventDuration
							defaultAllDayEventDuration={{ days: 1 }}
							events={filteredEvents}
							eventOrder='title,start'
							datesSet={(arg) => {
								setCurrentTitle(arg.view.title);
								setActiveView(arg.view.type);
							}}
							eventClick={(info) => {
								if (isMobile) {
									info.jsEvent.preventDefault();
									setMobileEventData(normalizeCalendarEvent(info.event));
								}
							}}
							eventContent={(info) => (
								<EventCard
									info={info}
									isMobile={isMobile}
									onHoverStart={openHoverTooltip}
									onHoverEnd={hideTooltipWithDelay}
								/>
							)}
							dayCellClassNames={(arg) => {
								const cellDay = toLocalDateKey(arg.date);
								return focusedTrainingDay === cellDay
									? ['course-calendar__focused-start-day']
									: [];
							}}
							eventClassNames={(arg) => {
								if (arg.event.extendedProps.type === 'holiday')
									return ['fc-event-holiday'];

								const segment = arg.event.extendedProps?.barSegment || 'single';
								return ['fc-event-course', `fc-event-course--${segment}`];
							}}
							eventDidMount={(info) => {
								const color =
									info.event.extendedProps?.trainingColor ||
									getSessionColor(
										info.event.id,
										getTitleKey(info.event.title),
										info.event.start,
									);
								info.el.style.setProperty('--event-accent', color);
							}}
						/>

						{!isMobile && hoveredEventData ? (
							<EventHoverTooltip
								data={hoveredEventData}
								relatedSessions={getRelatedSessions(hoveredEventData)}
								onCloseDelay={hideTooltipWithDelay}
								onKeepOpen={keepTooltipOpen}
								onSubscribe={handleSubscribeEvent}
								isAlreadySubscribed={isAlreadySubscribed}
								onSelectSession={handleSelectSessionFromTooltip}
							/>
						) : null}
					</div>
				</div>
			</div>

			<div
				className='subscription-floating'
				onMouseEnter={openCartHover}
				onMouseLeave={closeCartHoverWithDelay}>
				{toastOpen ? (
					<div className='subscription-toast'>
						<div className='subscription-toast__icon'>
							<ShoppingBasket size={18} />
						</div>

						<div className='subscription-toast__content'>
							<strong>Panier d’inscription</strong>
							<span>{toastText}</span>
						</div>
					</div>
				) : null}

				{subscriptionItems.length > 0 && (
					<div
						className={`subscription-cart-desktop ${
							isCartHoverOpen ? 'subscription-cart-desktop--visible' : ''
						}`}>
						<SubscriptionCart
							items={subscriptionItems}
							onRemove={handleRemoveSubscription}
							onOpenForm={() => setIsFormOpen(true)}
							onCloseMobile={() => setIsCartMobileOpen(false)}
							isMobileOpen={false}
							onMouseEnter={openCartHover}
							onMouseLeave={closeCartHoverWithDelay}
						/>
					</div>
				)}

				<button
					type='button'
					className='subscription-floating__button'
					onClick={() => setIsCartMobileOpen(true)}
					aria-label='Ouvrir le panier des inscriptions'>
					<ShoppingBasket size={22} />

					{subscriptionItems.length > 0 ? (
						<span className='subscription-floating__count'>
							{subscriptionItems.length}
						</span>
					) : null}
				</button>
			</div>

			<div
				className={`subscription-mobile-sheet ${
					isCartMobileOpen ? 'subscription-mobile-sheet--open' : ''
				}`}
				onClick={() => setIsCartMobileOpen(false)}>
				<div
					className='subscription-mobile-sheet__content'
					onClick={(e) => e.stopPropagation()}>
					<SubscriptionCart
						items={subscriptionItems}
						onRemove={handleRemoveSubscription}
						onOpenForm={() => setIsFormOpen(true)}
						onCloseMobile={() => setIsCartMobileOpen(false)}
						isMobileOpen
					/>
				</div>
			</div>

			{mobileEventData ? (
				<EventMobileModal
					eventData={mobileEventData}
					relatedSessions={getRelatedSessions(mobileEventData)}
					onClose={() => setMobileEventData(null)}
					onSubscribe={handleSubscribeEvent}
					isAlreadySubscribed={isAlreadySubscribed}
					onSelectSession={handleSelectSessionFromMobile}
				/>
			) : null}

			{isFormOpen && subscriptionItems.length > 0 ? (
				<SubscriptionFormModal
					items={subscriptionItems}
					formData={formData}
					onChange={handleFormChange}
					onSubmit={handleFormSubmit}
					onClose={() => setIsFormOpen(false)}
				/>
			) : null}

			{submitSuccess ? (
				<div className='subscription-success'>
					<CheckCircle2 size={20} />
					<span>Inscription envoyée avec succès.</span>
				</div>
			) : null}
		</section>
	);
}
